import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import HowItWorksSection from "@/components/sections/HowItWorksSection";
import TrustBadges from "@/components/sections/TrustBadges";
import FaqAccordion from "@/components/sections/FaqAccordion";
import { howToSchema } from "@/lib/structuredData";

const DETAILS = [
  { t: "1. Offer request (online)", d: "The form on this site is a request, not a purchase offer. You tell us the year, make, model, mileage, condition and title status. Nothing you enter commits you to anything." },
  { t: "2. Review and preliminary appraisal", d: "We read every request personally and call or text you back. We may ask for photos or a few more details so we can tell you up front whether the car is a fit and roughly where we expect to land." },
  { t: "3. In-person inspection", d: "We meet you in the Bay Area to look the car over, verify the VIN and title, check the mileage and take a short test drive if it runs. This usually takes 15 to 30 minutes." },
  { t: "4. Written offer", d: "After the inspection we give you an actual purchase offer in writing, with the price and the terms. You can take time to think it over. You are never obligated to accept." },
  { t: "5. Acceptance and paperwork", d: "If you accept, we complete the California title transfer together: you sign the title, we complete the odometer disclosure, and we help you file the DMV Notice of Transfer and Release of Liability." },
  { t: "6. Same-day payment", d: "You are paid the same day using the payment method agreed in writing. We do not take the vehicle until payment is arranged." },
];

export default function HowItWorks() {
  return (
    <div data-testid="page-how-it-works">
      <SEO
        title="How It Works — Carfornia Buyers"
        description="How selling your car to Carfornia Buyers works: online offer request, review, in-person inspection, written offer, paperwork and same-day payment."
        canonical="https://carforniabuyers.com/how-it-works"
        jsonLd={howToSchema(DETAILS)}
      />
      <section className="py-16 lg:py-24 bg-brand-surface">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">How it works</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight mt-3">
            From request to payment, explained plainly.
          </h1>
          <p className="text-lg text-slate-600 mt-5 max-w-2xl mx-auto">
            Here is exactly what happens after you submit your car, including the difference between requesting an offer and receiving one.
          </p>
        </div>
      </section>

      <HowItWorksSection />

      <section className="py-16 bg-white" data-testid="hiw-detail">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-8">
              {DETAILS.map((b) => (
                <div key={b.t}>
                  <h3 className="font-display text-xl font-bold text-slate-900">{b.t}</h3>
                  <p className="text-slate-600 mt-2 leading-relaxed">{b.d}</p>
                </div>
              ))}
            </div>
            <div className="bg-brand-surface rounded-3xl p-8 lg:p-10 border border-slate-100 lg:sticky lg:top-28">
              <h3 className="font-display text-2xl font-extrabold text-slate-900">What you’ll need</h3>
              <ul className="mt-5 space-y-3 text-slate-700">
                {[
                  "California Certificate of Title (or your lender’s information if there is a loan)",
                  "Valid government photo ID matching the name on the title",
                  "Current registration card, if you have it",
                  "All keys and remotes",
                  "Loan payoff information, if the car is financed",
                ].map((i) => (
                  <li key={i} className="flex gap-3"><span className="w-2 h-2 rounded-full bg-brand-blue mt-2 flex-shrink-0" /><span>{i}</span></li>
                ))}
              </ul>
              <p className="text-sm text-slate-500 mt-5">Lost title, salvage title or a lien? Tell us in the form. We will tell you honestly what we can do before anyone drives anywhere.</p>
              <Link to="/sell-your-car" className="block mt-7">
                <Button className="w-full h-12 bg-[#FF6B00] hover:bg-[#E66000] text-white font-bold rounded-xl">Request my offer <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-brand-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <TrustBadges />
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 text-center mb-10">Quick answers</h2>
          <FaqAccordion />
        </div>
      </section>
    </div>
  );
}
